import {
	DeskSelectProps,
	DeskSelectComponents,
	CheckableWidgetState,
	StyledPropsTheme,
	ModelDataChangedEvent,
	CodesWidget,
	CodeItem,
	CodesFilter
} from '@my-types';
import { React, Styled } from '@common/3rd';
import { Utils } from '@common';
import { StyledComponents } from '@styles';
import { CheckableWidget, DeskComponentRegistry } from '@component';
import { ErrorReminder, PassReminder } from './input-reminder';

class DeskSelect<P extends DeskSelectProps & {codesFilter?: CodesFilter; caret?: boolean, id?: string}, S extends CheckableWidgetState, C extends DeskSelectComponents>
	extends CheckableWidget<P, S, C>
	implements CodesWidget {
	selectRef: React.RefObject<any>;

	constructor(props: any, context?: any) {
		super(props, context);

		this.selectRef = React.createRef();
		if (this.isModelWatchable(this.getModel())) {
			const codesFilter = this.getCodesFilter();
			if (codesFilter && !Utils.isFunction(codesFilter)) {
				this.getModel().$$on(codesFilter.on, this.onParentValueChanged);
			}
		}
	}
	componentDidMount() {
		super.componentDidMount();
		const value = this.getValueFromModel();
		this.selectRef.current && (this.selectRef.current.value = value != null ? value : '');
	}
	componentDidUpdate(prevProps: any, prevState: any, prevContext?: any) {
		super.componentDidUpdate(prevProps, prevState, prevContext);
		const value = this.getValueFromModel();
		this.selectRef.current && (this.selectRef.current.value = value != null ? value : '');
	}

	componentWillUnmount() {
		super.componentWillUnmount();
		if (this.isModelWatchable(this.getModel())) {
			const codesFilter = this.getCodesFilter();
			if (codesFilter && !Utils.isFunction(codesFilter)) {
				this.getModel().$$off(codesFilter.on, this.onParentValueChanged);
			}
		}
	}

	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-select'
			})`
				display: flex;
				${props => this.renderCSS(props.theme)}
			`,
			Select: Styled(StyledComponents.SELECT)`
				flex-grow: 1;
				border: 1px solid ${props => props.theme.BORDER_COLOR_LIGHT};
				border-radius: ${props => props.theme.BORDER_RADIUS}px;
				line-height: ${props => props.theme.INPUT_LINE_HEIGHT}px;
				padding-left: ${props => props.theme.INPUT_HORIZONTAL_PADDING}px;
				max-width: ${props => props.theme.INPUT_MAX_WIDTH}px;
				${props => this.paintColor(props.theme)}
				&[disabled],
				&[readOnly],
				&[readonly] {
					background-color: transparent;
				}

				/* 获取焦点的时候, 如果有错误信息, 显示 */
				:focus + i > span {
					opacity: 1;
					animation-name: none;
				}
				&[data-caret=false] {
					background-image: none;
				}
			`
		} as C;
	}
	renderReminder(): JSX.Element | void {
		if (this.isCheckFailOnWhatever()) {
			// 有校验失败信息
			return <ErrorReminder msg={this.getCheckFailMessage()!} />;
		} else if (this.isCheckedAnyway()) {
			// 没有校验信息, 但是曾经校验过
			return <PassReminder />;
		}
	}
	renderOptions(): JSX.Element[] {
		const options = Utils.toArray(this.getCodes()).map((code, index) => {
			return (
				<option value={`${code.v}`} key={index}>
					{code.l}
				</option>
			);
		});
		if (this.isClearable()) {
			options.unshift(
				<option value="" key="-1">
					请选择
				</option>
			);
		}
		return options;
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box>
				<C.Select
					onChange={this.onValueChanged}
					onFocus={this.onSelectFocus}
					onBlur={this.onSelectBlur}
					disabled={!this.isEditable()}
					data-caret={this.caret()}
					ref={this.selectRef}
					id={this.getId()}
				>
					{this.renderOptions()}
				</C.Select>
				{this.renderReminder()}
			</C.Box>
		);
	}
	isClearable(): boolean {
		return Utils.isUndefined(this.props.clearable) ? true : (this.props.clearable as boolean);
	}
	caret(): boolean {
		return Utils.isUndefined(this.props.caret) ? true : (this.props.caret as boolean);
	}
	getId(): string {
		return this.props.id as string || '';
	}
	getCodes(): CodeItem[] {
		const codesFilter = this.getCodesFilter();
		if (codesFilter) {
			if (Utils.isFunction(codesFilter)) {
				return codesFilter.call(this, this.props.codes, this);
			} else {
				return codesFilter.filter.call(this, this.props.codes, this);
			}
		} else {
			return Utils.toArray(this.props.codes);
		}
	}

	protected getCodesFilter(): CodesFilter | undefined {
		return this.props.codesFilter;
	}

	paintColor(theme: StyledPropsTheme): string {
		if (this.isCheckFailOnWhatever()) {
			return `
				background-image: url(${StyledComponents.SelectCaret(theme.COLOR_ERROR)});
				color: ${theme.COLOR_ERROR};
				margin-bottom:30px;
			`;
		}
		const focus = document.activeElement === this.selectRef.current;
		return focus ? `background-image: url(${StyledComponents.SelectCaret(theme.COLOR_PRIMARY)});` : '';
	}
	focus(): this {
		this.selectRef.current.focus();
		return this;
	}
	protected setValueToDOM(value: any, propName?: string): this {
		if (propName == null || propName === this.getPropName()) {
			this.selectRef.current.value = value;
			// this.checkData();
		}
		return this;
	}
	protected doAfterCheckCompleted(): this {
		if (this.props.labellingParent) {
			this.props.labellingParent.onCheckCompleted(!this.isCheckFailOnWhatever());
		}
		return this;
	}
	private onValueChanged = (): void => {
		const value = this.selectRef.current.value;
		const oldValue = this.getValueFromModel();
		if (value === oldValue) {
			// 没有变化, 什么都不做
			return;
		}

		this.setValueToModel(value).checkData();
	};
	private onSelectFocus = (): void => {
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusGained();
		}
	};
	private onSelectBlur = (): void => {
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusLost();
		}
	};

	private onParentValueChanged = (event: ModelDataChangedEvent): void => {
		this.setValueToModel(null);
		this.forceUpdate();
	};
}

DeskComponentRegistry.register({
	dropdown: DeskSelect,
	select: DeskSelect
});

export { DeskSelect };
